/**
 * SyncAccionesRepository - Cola de acciones con ejecución server-side
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 *
 * La tabla `sync_acciones` se sincroniza como cualquier otra (INSERT →
 * pending_changes → push) y un trigger AFTER INSERT en Supabase ejecuta la
 * acción donde están todos los datos. Ver spec
 * docs/superpowers/specs/2026-09-25-alta-masiva-sin-caravana-design.md (§3).
 *
 * Append-only: el repositorio NO implementa update/delete de acciones — los
 * estados los setea el handler server-side y un trigger SQL protege los
 * estados terminales de los upserts de retry del sync.
 */

import { v4 as uuidv4 } from 'uuid';
import {
  ISyncAccionesRepository,
  ReasignarHistorialPayload,
  SyncAccion,
  SyncAccionTipo,
} from '../interfaces/ISyncAccionesRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

export class SyncAccionesRepository
  extends BaseRepository<SyncAccion, string>
  implements ISyncAccionesRepository {

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'sync_acciones', 'sync_acciones');

    // Sin esto getCurrentProfile() es el placeholder de BaseRepository que
    // siempre devuelve null (encolar necesita empresa_activa_id y user_id).
    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  async encolar(
    tipo: SyncAccionTipo,
    payload: ReasignarHistorialPayload,
  ): Promise<SyncAccion> {
    const profile = await this.getCurrentProfile();
    if (!profile?.empresa_activa_id || !profile?.user_id) {
      throw new Error('No hay empresa activa para encolar la acción');
    }

    const now = this.getCurrentTimestamp();
    const accion: SyncAccion = {
      id: uuidv4(),
      empresa_id: profile.empresa_activa_id,
      user_id: profile.user_id,
      tipo,
      payload,
      estado: 'pendiente',
      error: null,
      resultado: null,
      created_at: now,
      updated_at: now,
      ejecutada_at: null,
    };

    await this.db.insert('sync_acciones', accion);
    this.emitSync('INSERT', accion);
    return accion;
  }

  /** Alta directa (implementa el abstract de BaseRepository): delega en encolar. */
  async add(data: Omit<SyncAccion, 'id'>): Promise<string> {
    const accion = await this.encolar(data.tipo, data.payload);
    return accion.id;
  }

  async getFallidas(): Promise<SyncAccion[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(a => a.estado === 'fallida');
  }

  // ============================================================================
  // Operaciones bloqueadas a propósito (append-only server-side)
  // ============================================================================

  async update(): Promise<void> {
    throw new Error('sync_acciones es append-only: el estado lo setea el server');
  }

  async delete(): Promise<void> {
    throw new Error('sync_acciones es append-only: no se borran acciones');
  }
}
